import {MutableRefObject,  useContext,  useEffect, useState, useRef} from 'react';
import {useTypedFastStorage} from '../../hooks/useFastStorage';
import SecondaryButton from "../../ui/SecondaryButton";
import {ApplicableTableSettings} from "../../tableSettings";
import SmartTable from "../../uiControls/SmartTable";
import InlineInputControlRow from '../../ui/layout/InlineInputControlRow';
import TextBox from '../../ui/TextBox';
import IfmsDate from '../../ui/Date';
import Float from '../../ui/Float';
import ApplicationContext from '../../ApplicationContext';
import useGlobalOkDialog from '../../hooks/useGlobalOkDialog';
import { ApiResult } from '../../framework.core/data/ApiResult';
import { DateRenderer, DateTimeRenderer, FloatRenderer, NullableCheckboxIconRenderer, NumberedIndicatorRenderer, TextAlignLeftRenderer, TextRenderer } from '../../uiControls/SmartTableRenderers';
import { RedDateRenderer, RedFloatRenderer, RedTextAlignLeftRenderer, RedTextRenderer } from '../../uiControls/SmartTableRenderers';
import useTableNavigation from '../../ui/tables/useTableNavigation';
import { PaymentHistViewModel } from '../../fms.core/PaymentHistViewModel';
import { FmsSubMenu } from '../../constants/fmsSubMenu';
import { historyViewPaymentDetailsTable, historyViewPaymentMainTable } from './tableSettings';
import RadioButton from '../../ui/RadioButton';
import _ from 'lodash';

interface HistoryViewPaymentStorageKeys {
  modelStorage: string,
};

interface HistoryViewPaymentParams {
  onPopupClose?: () => void,
  jobId: number,
  jobType: string,
};

interface HistoryViewPaymentLoadedParams {
  historyViewPayment: PaymentHistViewModel,
  historyViewPaymentRef: MutableRefObject<PaymentHistViewModel | null>,
  setHistoryViewPayment: (x: Partial<PaymentHistViewModel>) => void,
  jobType: string,
  onPopupClose?: () => void,
};

function getHistoryViewPaymentStorageKeys(): HistoryViewPaymentStorageKeys {
  return {modelStorage: 'common-history-payment-view'}
}

function HistoryViewPaymentImpl(params: HistoryViewPaymentParams) {
  const appContext = useContext(ApplicationContext);
  const [showGlobalOkDialog] = useGlobalOkDialog();

  const {modelStorage} = getHistoryViewPaymentStorageKeys();
  const {jobId, jobType, onPopupClose} = params;

  const [inStorage, setInStorage, removeFromStorage, storageRef] = useTypedFastStorage<PaymentHistViewModel>(modelStorage, null);

  useEffect(() => {
      initialize();
      return () => removeFromStorage()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [jobId]
  );

  async function loadExisting(): Promise<PaymentHistViewModel|null> {
    const apiResult:ApiResult<PaymentHistViewModel> = await appContext.api.getPaymentHistory(jobId, jobType);
    if (!apiResult || !apiResult.success) {
      await showGlobalOkDialog(apiResult?.message || 'Cannot load data');
      if (onPopupClose) {
        onPopupClose();
      }
    }

    return apiResult.result;
  }

  async function initialize() {
    const data = await loadExisting();
    if (data) {
      setInStorage({...data});
    }
  }

  if (!inStorage) {
    return null;
  }

  return (
    <HistoryViewPaymentInitializedImpl
      historyViewPayment={inStorage}
      historyViewPaymentRef={storageRef}
      setHistoryViewPayment={setInStorage}
      onPopupClose={params.onPopupClose}
      jobType={jobType}
    />
  );
}

function HistoryViewPaymentInitializedImpl({
  historyViewPayment,
  historyViewPaymentRef,
  setHistoryViewPayment,
  jobType,
  onPopupClose,
}: HistoryViewPaymentLoadedParams) {
  // 250620. inhyung. Focus 역순으로 하기 위해 변경
  // const [selectedRow, setSelectedRow] = useState(historyViewPayment.detailList.length-1);
  const [selectedRow, setSelectedRow] = useState(0);
  const selectPaymentHist = historyViewPayment.histDetailList.filter(x => x.hdId === historyViewPayment.detailList[selectedRow].id);

  historyViewPayment.tempHistDetail = [];
  historyViewPayment.histDetailList.forEach(x=> {
    if (x.id === selectPaymentHist[0]?.id) {
      historyViewPayment.tempHistDetail.push(x);
    }
  });

  useTableNavigation('historyViewPaymentMainTable', setSelectedRow, setSelectedRow, [], false, true);

  // 250620. inhyung. 기존 data에 isChanged flag 추가.
  const dataWithIsChanged = historyViewPayment.detailList.map((row, idx, arr) => {
    const prev = idx < arr.length - 1 ? arr[idx + 1] : null;
    const isChanged = row.historyAction === 'Change' && prev;
    return {
      ...row,
      isChangedHistoryUser: isChanged && row.historyUser !== prev.historyUser,
      isChangedHistoryAction: isChanged,
      isChangedCurrency: isChanged && row.currency !== prev.currency,
      isChangedCardBank: isChanged && row.cardBank !== prev.cardBank,
      isChangedPostDate: isChanged && row.postDate !== prev.postDate,
      isChangedCheckNo: isChanged && row.checkNo !== prev.checkNo,
      isChangedCustName: isChanged && row.custName !== prev.custName,
      isChangedExRate: isChanged && row.exRate !== prev.exRate,
      isChangedRemark: isChanged && row.remark !== prev.remark,
      isChangedTotalAmount: isChanged && row.totalAmount !== prev.totalAmount,
    };
  });

  function getCustomerLabel(): string {
    if (jobType === FmsSubMenu.CPHistory) {
      return 'Customer';
    }
    if (jobType === FmsSubMenu.BPHistory) {
      return 'Vendor';
    }
    if (jobType === FmsSubMenu.MPHistory) {
      return 'Vendor';
    }
    if (jobType === FmsSubMenu.APHistory) {
      return 'Agent';
    }

    return '';
  }

  function getTotalLabel(): string {
    if (jobType === FmsSubMenu.CPHistory || jobType === FmsSubMenu.APHistory) {
      return 'Deposit Total';
    }
    if (jobType === FmsSubMenu.BPHistory) {
      return 'Payment Total';
    }
    if (jobType === FmsSubMenu.MPHistory) {
      return 'Payment Total';
    }

    return '';
  }

  function getAmountLabel(): string {
    if (jobType === FmsSubMenu.CPHistory || jobType === FmsSubMenu.APHistory) {
      return 'Received Total';
    }
    if (jobType === FmsSubMenu.BPHistory) {
      return 'Paid Total';
    }
    if (jobType === FmsSubMenu.MPHistory) {
      return 'Paid Total';
    }

    return '';
  }

  function getEntityLabel(): string {
    if (jobType === FmsSubMenu.CPHistory || jobType === FmsSubMenu.APHistory) {
      return 'Bank Entity';
    }
    if (jobType === FmsSubMenu.BPHistory) {
      return 'Bank Entity';
    }
    if (jobType === FmsSubMenu.MPHistory) {
      return 'Credit Card';
    }

    return '';
  }

  function getReceivedFromLabel(): string {
    if (jobType === FmsSubMenu.CPHistory || jobType === FmsSubMenu.APHistory) {
      return 'Received From';
    }
    if (jobType === FmsSubMenu.BPHistory) {
      return 'Pay To';
    }
    if (jobType === FmsSubMenu.MPHistory) {
      return ''; // Not visible
    }

    return '';
  }

  function getRefNoLabel(): string {
    if (jobType === FmsSubMenu.CPHistory || jobType === FmsSubMenu.BPHistory || jobType === FmsSubMenu.APHistory) {
      if (historyViewPayment.etf === "1") {
        return 'W/T #';
      }
      // else if (historyViewPayment.etf === "3") {
      //   return 'ACH #';
      // }
    }

    return 'Check #';
  }

  // function getDateLabel(): string {
  //   if (jobType === FmsSubMenu.CPHistory || jobType === FmsSubMenu.APHistory) {
  //     return 'Deposit Date';
  //   }
  //   if (jobType === FmsSubMenu.BPHistory) {
  //     return 'Clear Date';
  //   }
  //   if (jobType === FmsSubMenu.MPHistory) {
  //     return 'Clear Date';
  //   }

  //   return '';
  // }

  let tableSettings = { ...historyViewPaymentMainTable, columns: historyViewPaymentMainTable.columns.map(c => ({ ...c })) };
  const columns = _.keyBy(tableSettings.columns, 'accessor');
  let hcolPercent = 0
  if (jobType === FmsSubMenu.MPHistory) {
    const removeColumns = ['custName', 'draft'];
    for (const accessor in columns) {
      if (removeColumns.indexOf(accessor) >= 0) {
        hcolPercent += columns[accessor].initialWidthPercentage ?? 0;
        tableSettings.columns.splice(tableSettings.columns.indexOf(columns[accessor]), 1);
      }
    }

    columns['remark'].initialWidthPercentage = (columns['remark'].initialWidthPercentage ?? 0) + hcolPercent;
  }
  
  const mainTableOptions: ApplicableTableSettings = {
    ...tableSettings,
    selectable: true,
    rowProps: (row: any) => ({onClick: () => setSelectedRow(row.index)}),
    selectedRowIndex: selectedRow,
    customTableId: 'historyViewPaymentMainTable',
    tableName: 'historyViewPaymentMainTable',
    data: dataWithIsChanged,
    // data: historyViewPayment.detailList,

    // 250626. inhyung. Date는 Red 표시 제외 및 컬럼 Title 수정.
    rowRenderers: [
      { accessor: 'indicator', Cell: NumberedIndicatorRenderer },
      { accessor: 'historyDate', Cell: DateTimeRenderer },
      { accessor: 'historyUser', Cell: (props: any) => props.row.original.isChangedHistoryUser ? <RedTextAlignLeftRenderer {...props} /> : <TextAlignLeftRenderer {...props} />, },
      { accessor: 'historyAction', Cell: (props: any) => props.row.original.isChangedHistoryAction ? <RedTextRenderer {...props} /> : <TextRenderer {...props} />, },
      { accessor: 'typeDesc', Cell: TextRenderer },
      { accessor: 'cardBank', Cell: (props: any) => props.row.original.isChangedCardBank ? <RedTextAlignLeftRenderer {...props} /> : <TextAlignLeftRenderer {...props} />, Header: () => <span>{getEntityLabel()}</span> },
      { accessor: 'postDate', Cell: (props: any) => props.row.original.isChangedPostDate ? <RedDateRenderer {...props} /> : <DateRenderer {...props} />, },
      { accessor: 'checkNo', Cell: (props: any) => props.row.original.isChangedCheckNo ? <RedTextAlignLeftRenderer {...props} /> : <TextAlignLeftRenderer {...props} />, Header: () => <span>{getRefNoLabel()}</span> },
      { accessor: 'currency', Cell: (props: any) => props.row.original.isChangedCurrency ? <RedTextRenderer {...props} /> : <TextRenderer {...props} />, },
      { accessor: 'exRate', Cell: (props: any) => props.row.original.isChangedExRate ? <RedFloatRenderer {...props} decimals={4} /> : <FloatRenderer {...props} decimals={4} />,},
      { accessor: 'block', Cell: NullableCheckboxIconRenderer },
      { accessor: 'draft', Cell: NullableCheckboxIconRenderer },
      { accessor: 'custName', Cell: (props: any) => props.row.original.isChangedCustName ? <RedTextAlignLeftRenderer {...props} /> : <TextAlignLeftRenderer {...props} />, Header: () => <span>{getReceivedFromLabel()}</span> },
      { accessor: 'remark', Cell: (props: any) => props.row.original.isChangedRemark ? <RedTextAlignLeftRenderer {...props} /> : <TextAlignLeftRenderer {...props} />, },
      { accessor: 'totalAmount', Cell: (props: any) => props.row.original.isChangedTotalAmount ? <RedFloatRenderer {...props} /> : <FloatRenderer {...props} />, Header: () => <span>{getTotalLabel()}</span> },
    ],
    isModalTable: true,
  };

  const paymentDetailTableOptions: ApplicableTableSettings = {
    ...historyViewPaymentDetailsTable,
    selectable: true,
    tableName: 'historyViewPaymentDetailsTable',
    // data: selectedRow >= 0 ? historyViewPayment.histDetailList.filter(x => x.hdId === historyViewPayment.detailList[selectedRow].id) : [],
    data: selectedRow >= 0 ? historyViewPayment.tempHistDetail : [],
    rowRenderers: [
      { accessor: 'indicator', Cell: NumberedIndicatorRenderer },
      {accessor: 'status', Cell: TextAlignLeftRenderer },
      {accessor: 'customer', Cell: TextAlignLeftRenderer, Header: () => <span>{getCustomerLabel()}</span> },
      {accessor: 'invoiceDate', Cell: DateRenderer },
      {accessor: 'invoiceNo', Cell: TextAlignLeftRenderer },
      {accessor: 'accountCode', Cell: TextRenderer },
      {accessor: 'description', Cell:TextAlignLeftRenderer },
      {accessor: 'amount', Cell: FloatRenderer, Header: () => <span>{getAmountLabel()}</span> },
    ],
    rowProps: (row:any) => {
      if (historyViewPayment.tempHistDetail[row.index].status === "Original") {
        return {
          style: {
            color: 'red'
          }
        }
      }
    },
    isModalTable: true,
  };

  return (
    <div className='report-popup'>
      <div style={{display: 'flex'}}>
        <div style={{width: '280px'}}>
          <InlineInputControlRow label={getCustomerLabel()} mode="w100">
            <TextBox disabled value={historyViewPayment.entity} style={{border: '1px solid #696969'}} isModal />
          </InlineInputControlRow>
        </div>

        <div style={{marginLeft: '40px', width: '340px'}}>
          <InlineInputControlRow label={jobType !== FmsSubMenu.MPHistory ? 'Payment Type' : 'CC Ref #'} mode="w100">
            {jobType !== FmsSubMenu.MPHistory
              ?
                <div style={{display: 'flex'}}>
                  <div style={{width: '80px'}}>
                    <RadioButton disabled group="payment-type" label="Check" reverse checked={historyViewPayment.etf === "0"} />
                  </div>
                  <div style={{marginLeft: '10px', width: '80px'}}>
                    <RadioButton disabled group="payment-type" label="ACH" reverse checked={historyViewPayment.etf === "3"} />
                  </div>
                  <div style={{marginLeft: '10px', width: '80px'}}>
                    <RadioButton disabled group="payment-type" label="W/T" reverse checked={historyViewPayment.etf === "1"} />
                  </div>
                  <div style={{marginLeft: '10px', width: '80px'}}>
                    <RadioButton disabled group="payment-type" label="Other" reverse checked={historyViewPayment.etf === "4"} />
                  </div>
                </div>
              :
                <TextBox disabled value={historyViewPayment.ccRefNo} style={{border: '1px solid #696969'}} isModal/>
            }
          </InlineInputControlRow>
        </div>

        <div style={{marginLeft: '40px', width: '160px'}}>
          <InlineInputControlRow label="Accrual Date" mode="w80">
            <IfmsDate disabled value={historyViewPayment.postDate} style={{border: '1px solid #696969'}} />
          </InlineInputControlRow>
        </div>

        <div style={{marginLeft: '40px', width: '180px'}}>
          {/* <InlineInputControlRow label={getDateLabel()} mode="w100"> */}
          <InlineInputControlRow label="Clear Date" mode="w100">
            <IfmsDate disabled value={historyViewPayment.depositDate} style={{border: '1px solid #696969'}} />
          </InlineInputControlRow>
        </div>
      </div>

      <div style={{display: 'flex'}}>
        <div style={{width: '280px'}}>
          <InlineInputControlRow label="Bank Entity" mode="w100">
            <TextBox disabled value={historyViewPayment.bankName} style={{border: '1px solid #696969'}} isModal/>
          </InlineInputControlRow>
        </div>

        <div style={{marginLeft: '40px', width: '300px'}}>
          <InlineInputControlRow label={getRefNoLabel()} mode="w100">
            <div style={{width: '80px'}}>
              <TextBox disabled value={historyViewPayment.checkNo} style={{border: '1px solid #696969'}} isModal/>
            </div>
          </InlineInputControlRow>
        </div>

        <div style={{marginLeft: '80px', width: '160px'}}>
          <InlineInputControlRow label={getTotalLabel()} mode="w80">
            <Float disabled value={historyViewPayment.total} style={{border: '1px solid #696969'}} isModal/>
          </InlineInputControlRow>
        </div>

        {jobType === FmsSubMenu.MPHistory &&
          <div style={{marginLeft: '40px', width: '280px'}}>
            <InlineInputControlRow label="Credit Card" mode="w100">
              <TextBox disabled value={historyViewPayment.creditCard} style={{border: '1px solid #696969'}} isModal />
            </InlineInputControlRow>
          </div>
        }
      </div>

      <SmartTable {...mainTableOptions} />

      <div style={{display: 'flex'}}>
        {/* <div style={{margin: 'auto'}}></div> */}
        <div style={{width: '190px', textAlign: 'right', paddingRight: '10px'}}>
          Payment Detail
        </div>
        <div style={{width: '860px'}}>
          <SmartTable {...paymentDetailTableOptions} />
        </div>

      </div>

      <div className='report-footer'>
        <SecondaryButton
          size="regular"
          style={{width: '90px'}}
          onClick={onPopupClose}>
          CLOSE
        </SecondaryButton>
      </div>
    </div>
  );
}

const HistoryViewPayment = ({
  onPopupClose,
  jobId,
  jobType,
}: { onPopupClose?: () => void, jobId: number, jobType: string }) => {
  /**
   * Implementation note:
   *   key is important in this case in order to force full List view rerender for different parameters.
   */

  return (
    <HistoryViewPaymentImpl jobType={jobType} jobId={jobId} onPopupClose={onPopupClose} key={`historyViewPayment-details`}/>
  );
};
export default HistoryViewPayment;
